import React, { useState } from 'react';
import { FaRegWindowClose } from "react-icons/fa";
import { Plus } from "lucide-react";
import AlertMsg from '../../../component/AlertMsg/AlertMsg';
import Loading from '../../../component/Loading/Loading';

const AddHome = () => {
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState({ visible: false, message: '', type: '' });
    const [showAddForm, setShowAddForm] = useState(true);

    // Form state
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [mobile, setMobile] = useState('');
    const [accountNo, setAccountNo] = useState('');
    const [moveInDate, setMoveInDate] = useState('');
    const [address, setAddress] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [country, setCountry] = useState('');
    const [zipCode, setZipCode] = useState('');
    const [errors, setErrors] = useState({});

    const validateForm = () => {
        const newErrors = {};

        if (!firstName.trim()) {
            newErrors.firstName = 'First name is required';
        }

        if (!lastName.trim()) {
            newErrors.lastName = 'Last name is required';
        }

        if (!email.trim()) {
            newErrors.email = 'Email address is required';
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        if (!mobile.trim()) {
            newErrors.mobile = 'Mobile number is required';
        } else if (!/^\d{10}$/.test(mobile)) {
            newErrors.mobile = 'Please enter a valid 10-digit mobile number';
        }

        if (!accountNo.trim()) {
            newErrors.accountNo = 'Account number is required';
        }

        if (!moveInDate) {
            newErrors.moveInDate = 'Move-in date is required';
        }

        if (!address.trim()) {
            newErrors.address = 'Residential address is required';
        }

        if (!city.trim()) {
            newErrors.city = 'City is required';
        }

        if (!state.trim()) {
            newErrors.state = 'State is required';
        }

        if (!country.trim()) {
            newErrors.country = 'Country is required';
        }

        if (!zipCode.trim()) {
            newErrors.zipCode = 'Zip code is required';
        } else if (!/^\d{5}$/.test(zipCode)) {
            newErrors.zipCode = 'Please enter a valid 5-digit zip code';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const isValid = validateForm();
        if (isValid) {
            setLoading(true);
            // Simulate API call
            setTimeout(() => {
                setLoading(false);
                setAlert({ visible: true, message: 'Home added successfully!', type: 'success' });
                handleCancel();
            }, 1000);
        }
    };

    const handleCancel = () => {
        setFirstName('');
        setLastName('');
        setEmail('');
        setMobile('');
        setAccountNo('');
        setMoveInDate('');
        setAddress('');
        setCity('');
        setState('');
        setCountry('');
        setZipCode('');
        setErrors({});
        setShowAddForm(false);
    };

    return (
        <div className="bg-gray-50 min-h-screen md:p-4">
            {loading && <Loading />}
            {!loading && (
                <div className="max-w-7xl mx-auto">
                    {alert.visible && (
                        <AlertMsg
                            message={alert.message}
                            type={alert.type}
                            onClose={() => setAlert({ ...alert, visible: false })}
                        />
                    )}

                    <div className="flex justify-between items-center mb-6">
                        <h1 className="text-2xl font-bold text-gray-800">Add Home</h1>
                        <div className="flex items-center">
                            <button
                                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md flex items-center gap-2"
                                onClick={() => setShowAddForm(true)}
                            >
                                <Plus size={18} />
                                <span>Add</span>
                            </button>
                        </div>
                    </div>

                    {showAddForm && (
                        <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
                            <form className="p-4" onSubmit={handleSubmit}>
                                <div className="flex flex-wrap gap-6 mb-6">
                                    <div className="w-full md:w-[calc(50%-1.5rem)]">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            First Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={firstName}
                                            onChange={(e) => setFirstName(e.target.value)}
                                            className={`w-full rounded-md border ${errors.firstName ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                        />
                                        {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
                                    </div>

                                    <div className="w-full md:w-[calc(50%-1.5rem)]">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Last Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={lastName}
                                            onChange={(e) => setLastName(e.target.value)}
                                            className={`w-full rounded-md border ${errors.lastName ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                        />
                                        {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
                                    </div>

                                    <div className="w-full md:w-[calc(50%-1.5rem)]">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Email <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            value={email}
                                            pattern="[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$"
                                            onChange={(e) => setEmail(e.target.value)}
                                            className={`w-full rounded-md border ${errors.email ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                        />
                                        {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                                    </div>

                                    <div className="w-full md:w-[calc(50%-1.5rem)]">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Mobile <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={mobile}
                                            onChange={(e) => {
                                                const input = e.target.value;
                                                if (/^\d{0,10}$/.test(input)) {
                                                    setMobile(input);
                                                }
                                            }}
                                            className={`w-full rounded-md border ${errors.mobile ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                        />
                                        {errors.mobile && <p className="text-red-500 text-xs mt-1">{errors.mobile}</p>}
                                    </div>

                                    <div className="w-full md:w-[calc(50%-1.5rem)]">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Account No <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            value={accountNo}
                                            onChange={(e) => setAccountNo(e.target.value)}
                                            className={`w-full rounded-md border ${errors.accountNo ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                        />
                                        {errors.accountNo && <p className="text-red-500 text-xs mt-1">{errors.accountNo}</p>}
                                    </div>

                                    <div className="w-full md:w-[calc(50%-1.5rem)]">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Move In Date <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="date"
                                            value={moveInDate}
                                            onChange={(e) => setMoveInDate(e.target.value)}
                                            className={`w-full rounded-md border ${errors.moveInDate ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                        />
                                        {errors.moveInDate && <p className="text-red-500 text-xs mt-1">{errors.moveInDate}</p>}
                                    </div>

                                    <div className="w-full md:w-[calc(50%-1.5rem)]">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Residential Address <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={address}
                                            onChange={(e) => setAddress(e.target.value)}
                                            className={`w-full rounded-md border ${errors.address ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                        />
                                        {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
                                    </div>

                                    <div className="w-full md:w-[calc(50%-1.5rem)]">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            City <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={city}
                                            onChange={(e) => setCity(e.target.value)}
                                            className={`w-full rounded-md border ${errors.city ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                        />
                                        {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
                                    </div>

                                    <div className="w-full md:w-[calc(50%-1.5rem)]">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            State <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={state}
                                            onChange={(e) => setState(e.target.value)}
                                            className={`w-full rounded-md border ${errors.state ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                        />
                                        {errors.state && <p className="text-red-500 text-xs mt-1">{errors.state}</p>}
                                    </div>

                                    <div className="w-full md:w-[calc(50%-1.5rem)]">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Country <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={country}
                                            onChange={(e) => setCountry(e.target.value)}
                                            className={`w-full rounded-md border ${errors.country ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                        />
                                        {errors.country && <p className="text-red-500 text-xs mt-1">{errors.country}</p>}
                                    </div>

                                    <div className="w-full md:w-[calc(50%-1.5rem)]">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Zip Code <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={zipCode}
                                            onChange={(e) => setZipCode(e.target.value)}
                                            className={`w-full rounded-md border ${errors.zipCode ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                        />
                                        {errors.zipCode && <p className="text-red-500 text-xs mt-1">{errors.zipCode}</p>}
                                    </div>
                                </div>

                                <div className="flex justify-between items-center">
                                    <div className="text-sm text-red-500">* indicates required field</div>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                            onClick={handleCancel}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                                        >
                                            Submit
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* You can add table view here if needed, similar to AddAmenity */}
                </div>
            )}
        </div>
    );
};

export default AddHome;